import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material"
import Autocomplete from "@mui/material/Autocomplete"
import { useEffect, useState } from "react"
import {
  selectEnteredInvoiceInformation,
  selectLGInternalUser,
  updateInvoiceDetail,
} from "../../activeScreenSlice"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { useDebounce } from "../../hooks/useDebounce"
import useShowError from "../../hooks/useShowError"
import { getSupplierSearch } from "../../service/Supplier"
import { LinkWrapper } from "../DependentFields/DependentFields.styles"
import {
  StyledErrorIcon,
  StyledIcon,
  StyledStatusIcon,
} from "../FormTextBox/FormTextbox.styles"
// @ts-ignore
import LinkIcon from "../DependentFields/link.svg?react"
// @ts-ignore
import LineIcon from "../DependentFields/line.svg?react"
import {
  AutocompleteWrapper,
  StyledTextField,
  SupplierLookupWrapper,
} from "./SupplierLookup.styles"

interface SupplierSite {
  id: number
  name: string
}
interface SupplierOption {
  supplierName: string | undefined
  supplierNumber: string | undefined
  supplierId: number | undefined
  supplierSites: SupplierSite[]
}

export default function SupplierLookup() {
  const showError = useShowError()
  const dispatch = useAppDispatch()
  const LGInternalUser = useAppSelector(selectLGInternalUser);
  const invoiceInputs = useAppSelector(selectEnteredInvoiceInformation)

  const [nameTextEntry, setNameTextEntry] = useState(invoiceInputs.supplierName)
  const debouncedNameTextEntry = useDebounce(nameTextEntry)
  const [numberTextEntry, setNumberTextEntry] = useState(
    invoiceInputs.supplierNumber,
  )
  const debouncedNumberTextEntry = useDebounce(numberTextEntry)
  const [searchEnabled, setSearchEnabled] = useState(false)
  const [searchResults, setSearchResults] = useState<SupplierOption[]>([
    {
      supplierName: "",
      supplierNumber: "",
      supplierId: undefined,
      supplierSites: [],
    },
  ])

  function handleUpdateStore(key: string, value?: any) {
    dispatch(updateInvoiceDetail({ key: key, value: value }))
  }

  useEffect(() => {
    if (
      debouncedNumberTextEntry !== undefined &&
      debouncedNumberTextEntry.length < 1
    ) {
      setSearchResults([])
      handleUpdateStore("supplierName", undefined)
      handleUpdateStore("supplierNumber", undefined)
      handleUpdateStore("supplierId", undefined)
      handleUpdateStore("supplierSiteId", undefined)
      handleUpdateStore("supplierSiteOptions", undefined)
    }
    if (!searchEnabled) return
    if (debouncedNumberTextEntry && debouncedNumberTextEntry.length >= 1) {
      getSupplierSearch("supplierNumber", debouncedNumberTextEntry, invoiceInputs.opcoSlug)
        .then((res) => {
          const options = res.map((opt) => {
            return {
              supplierName: opt.supplier_name,
              supplierNumber: opt.supplier_number,
              supplierId: opt.id,
              supplierSites: opt.supplier_sites.map((site) => {
                return {
                  id: site.id,
                  name: site.site_code,
                }
              }),
            }
          })
          setSearchResults(options)
        })
        .catch((error) => {
          showError(
            "An error occurred while searching for supplier number: " +
              error.name +
              " " +
              error.message,
          )
        })
    }
  }, [showError, debouncedNumberTextEntry])

  useEffect(() => {
    if (
      debouncedNameTextEntry !== undefined &&
      debouncedNameTextEntry.length < 3
    ) {
      setSearchResults([])
      handleUpdateStore("supplierName", undefined)
      handleUpdateStore("supplierNumber", undefined)
      handleUpdateStore("supplierId", undefined)
      handleUpdateStore("supplierSiteId", undefined)
      handleUpdateStore("supplierSiteOptions", undefined)
    }
    if (!searchEnabled) return
    if (debouncedNameTextEntry && debouncedNameTextEntry.length >= 3) {
      getSupplierSearch("supplierName", debouncedNameTextEntry, invoiceInputs.opcoSlug)
        .then((res) => {
          const options = res.map((opt) => {
            return {
              supplierName: opt.supplier_name,
              supplierNumber: opt.supplier_number,
              supplierId: opt.id,
              supplierSites: opt.supplier_sites.map((site) => {
                return {
                  id: site.id,
                  name: site.site_code,
                }
              }),
            }
          })
          setSearchResults(options)
        })
        .catch((error) => {
          showError(
            "An error occurred while searching for supplier name: " +
              error.message,
          )
        })
    } else {
      setSearchResults([])
    }
  }, [showError, debouncedNameTextEntry])

  function handleValueUpdate(
    field: "supplierName" | "supplierNumber",
    value: string | null | undefined,
  ) {
    const matchingOptions = searchResults.filter(
      (option) => option[field] === value,
    )
    if (matchingOptions) {
      handleUpdateStore("supplierName", matchingOptions[0].supplierName)
      handleUpdateStore("supplierNumber", matchingOptions[0].supplierNumber)
      handleUpdateStore("supplierId", matchingOptions[0].supplierId)
      handleUpdateStore("supplierSiteId", undefined)
      handleUpdateStore("supplierSiteOptions", matchingOptions[0].supplierSites)
    }
  }

  const validationState = invoiceInputs.supplierNumber ? "ok" : "error"

  return (
    <>
      <SupplierLookupWrapper>
        <LinkWrapper
          validationState={validationState}
          sx={{ paddingTop: "8px", paddingRight: "8px" }}
        >
          <LineIcon transform={"scale(1.5)"} />
          <LinkIcon transform={"scale(1.5)"} />
          <LineIcon transform={"rotate(180), scale(1.5)"} />
        </LinkWrapper>
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
          <AutocompleteWrapper>
            <Autocomplete
              value={invoiceInputs.supplierName || ""}
              onChange={(event, newValue) => {
                handleValueUpdate("supplierName", newValue)
              }}
              inputValue={nameTextEntry}
              onInputChange={(event, newInputValue) => {
                if (event !== null) {
                  setSearchEnabled(true)
                } else {
                  setSearchEnabled(false)
                }

                setNameTextEntry(newInputValue)
              }}
              options={searchResults.map((res) => res.supplierName)}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  label="Supplier name"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    ...params.InputProps,
                    style: {
                      height: "36px",
                    },
                  }}
                />
              )}
              style={{ width: "100%", paddingRight: "12px" }}
              disabled={LGInternalUser}
            />
            {validationState && (
              <StyledIcon style={{ paddingRight: "8px" }}>
                {validationState === "ok" && <StyledStatusIcon />}
                {validationState === "error" && <StyledErrorIcon />}
              </StyledIcon>
            )}
          </AutocompleteWrapper>
          <AutocompleteWrapper>
            <Autocomplete
              value={invoiceInputs.supplierNumber || ""}
              onChange={(event, newValue) => {
                handleValueUpdate("supplierNumber", newValue)
              }}
              inputValue={numberTextEntry}
              onInputChange={(event, newInputValue) => {
                if (event !== null) {
                  setSearchEnabled(true)
                } else {
                  setSearchEnabled(false)
                }
                setNumberTextEntry(newInputValue)
              }}
              options={searchResults.map((option) => option.supplierNumber)}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  label="Supplier number"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    ...params.InputProps,
                    style: {
                      height: "36px",
                    },
                  }}
                />
              )}
              style={{ width: "100%", paddingRight: "12px" }}
              disabled={LGInternalUser} 
            />
            {validationState && (
              <StyledIcon style={{ paddingRight: "8px" }}>
                {validationState === "ok" && <StyledStatusIcon />}
                {validationState === "error" && <StyledErrorIcon />}
              </StyledIcon>
            )}
          </AutocompleteWrapper>
        </Box>
      </SupplierLookupWrapper>
      <Box sx={{ display: "flex", gap: "9px" }}>
        <Box sx={{ width: "235px" }}>
          <FormControl fullWidth>
            <InputLabel>Supplier site</InputLabel>
            <Select
              value={invoiceInputs.supplierSiteId}
              label="Supplier site"
              onChange={(e, v) => {
                handleUpdateStore("supplierSiteId", e.target.value)
              }}
              disabled={LGInternalUser}
            >
              {invoiceInputs.supplierSiteOptions &&
                invoiceInputs.supplierSiteOptions.map((site) => (
                  <MenuItem value={site.id} key={site.id}>
                    {site.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
        <StyledIcon>
          {invoiceInputs.supplierSiteId && <StyledStatusIcon />}
          {invoiceInputs.supplierSiteId === undefined && <StyledErrorIcon />}
        </StyledIcon>
      </Box>
    </>
  )
}
